import { useDispatch, useSelector } from "react-redux";
import { useReactFlow, getIncomers, getOutgoers, getConnectedEdges, reconnectEdge } from "reactflow";
import { COMMON_COMPONENT_TYPES, NODE_TYPES, EDGE_TYPES, CANVAS_DIRECTIONS } from "constants/flowDesigner";
import { updateFlowSteps } from "redux/actions/flowDesigner/flowsActionCreator";
import useComponentIdGenerator from "hooks/useComponentIdGenerator";
import commonComponentsMockData from "layouts/Flow Designer/data/commonComponentsMockData";
import { generateEdge, addLabelNodes, openNewNodeProperties } from "layouts/Flow Designer/helpers/common";
import { addStepToContext, addContextVariable } from "redux/actions/flowDesigner/editorContextActionCreator";
import { useHandleFloatingEdge } from "hooks/useHandleFloatingEdge";
import { getConnectorVersions } from "api/ConnectorVersions";
import { fetchConnectorSchema } from "helpers/connectorSchema";
import { useAuth0 } from "@auth0/auth0-react";
import { getLatestVersionObject } from "helpers/versions";
import useAutoLayout from "hooks/useAutoLayout";
import { flushSync } from "react-dom";
import useGetBranchNodes from "hooks/useGetBranchNodes";

const isMultiBranchNode = (nodeData) => {
   return nodeData?.type === COMMON_COMPONENT_TYPES.CONDITIONAL || nodeData?.type === COMMON_COMPONENT_TYPES.SELECTION;
};

const useFlowStepConnection = () => {
   const dispatch = useDispatch();
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const { generateComponentId, generateNodeId } = useComponentIdGenerator();
   const reactFlowInstance = useReactFlow();
   const { getNode, getNodes, getEdges, setNodes, setEdges } = reactFlowInstance;
   const { createFloatingEdge } = useHandleFloatingEdge();
   const { user } = useAuth0();
   const { isVerticalCanvasDirection } = useSelector((store) => store?.designer?.settings);
   const { updateLayout } = useAutoLayout({
      direction: isVerticalCanvasDirection ? CANVAS_DIRECTIONS.VERTICAL : CANVAS_DIRECTIONS.HORIZONTAL,
      manualRun: true
   });
   const { getChildNodeIdsBeforeMergeNode } = useGetBranchNodes();

   const tryToGenerateNodeData = async (step, sourceNodeData) => {
      try {
         let nodeData;

         if (step.type) {
            nodeData = {
               label: step.label,
               type: step.type
            };
         } else {
            const versions = await getConnectorVersions(user?.stripeId, step.id ?? step.componentId);

            const latestVersion = getLatestVersionObject(versions);

            nodeData = {
               componentId: step.id ?? step.componentId,
               configFiles: latestVersion.config_files,
               version: latestVersion.version,
               connectorVersionUpdatedDatetime: latestVersion.updated_datetime || null,
               label: step.name ?? step.label,
               logo: step.logo,
               componentFiles: ""
            };
         }

         return await addNodeDataFields(nodeData, sourceNodeData, step.type);
      } catch (error) {
         console.error(error);
         return {};
      }
   };

   const addNodeDataFields = async (nodeData, sourceNodeData, type) => {
      const isBranch = isMultiBranchNode(sourceNodeData) || sourceNodeData?.isBranch;

      if (type) {
         nodeData.payloadable = !!commonComponentsMockData[type]?.payloadable;
      } else {
         nodeData.componentFiles = {
            schema: await fetchConnectorSchema(nodeData.configFiles)
         };
         nodeData.payloadable = !!nodeData.componentFiles?.schema?.payloadable;
         nodeData.isConnectorExist = true;
      }

      nodeData.customLabel = "";
      nodeData.flowElement = {};
      nodeData.expanded = true;
      nodeData.isBranch = isBranch;
      nodeData.componentKey = generateComponentId(nodeData);
      nodeData.notes = "";

      return nodeData;
   };

   const tryToAddStepToCanvas = (newSourceId, targetId, newNode) => {
      try {
         const labelSteps = checkAndAddLabelNodes(newNode, targetId);
         addStepToCanvas(newSourceId, targetId, newNode, labelSteps);
      } catch (error) {
         console.error(error);
      }
   };

   const checkAndAddLabelNodes = (newNode, targetId) => {
      if (isMultiBranchNode(newNode.data)) {
         const isConditional = newNode.data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL;
         const params = {
            position: newNode.position,
            getId: generateNodeId,
            sourceId: newNode.id,
            targetId: targetId,
            countOfNodesToAdd: isConditional ? 2 : 3,
            isConditional: isConditional
         };
         return addLabelNodes(params);
      }
      return {};
   };

   const addStepToCanvas = (newSourceId, targetId, newNode, labelSteps) => {
      const newTargetId = newNode.id;
      const incomingEdge = generateEdge(newSourceId, newTargetId);
      let { labelNodes = [], labelEdges = [] } = labelSteps;
      let outgoingEdge = null;
      let newEdges = [incomingEdge];

      if (newNode.data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL || newNode.data?.type === COMMON_COMPONENT_TYPES.SELECTION) {
         const res = addHiddenMergeNode(newNode, labelSteps, targetId);
         labelNodes = res.labelNodes;
         labelEdges = res.labelEdges;
      }

      if (labelEdges.length) {
         newEdges = [...newEdges, ...labelEdges];
      } else {
         outgoingEdge = generateEdge(newTargetId, targetId);
         newEdges = [...newEdges, outgoingEdge];
      }

      setNewNodes(newNode, labelNodes);

      // Add floating edge for GOTO node for the cloned node
      if (newNode.data?.type === COMMON_COMPONENT_TYPES.GOTO && newNode.data.clone && newNode.data.flowElement?.data?.nodeId?.value) {
         const floatingEdge = createFloatingEdge(newNode.id, newNode.data.flowElement.data.nodeId.value);
         newEdges = [...newEdges, floatingEdge];
      }

      filterAndSetNewEdges(newSourceId, newTargetId, targetId, newEdges);

      dispatch(
         addStepToContext(
            newNode.data?.componentKey,
            {
               nodeId: newNode.id,
               type: newNode.type === "custom" ? "connector" : "component",
               inputData: {},
               outputData: {}
            },
            currentSavedFlow?.name
         )
      );

      if (newNode.data?.type === COMMON_COMPONENT_TYPES.VARIABLES) {
         dispatch(addContextVariable(newNode.data?.componentKey, currentSavedFlow?.name));
      }

      openNewNodeProperties(newNode.id);
      updateSteps(newNode.id, newNode.data?.type);
   };

   const addHiddenMergeNode = (newNode, labelSteps, targetId) => {
      const mergeNodeid = generateNodeId();
      const position = { x: newNode?.position.x, y: newNode?.position.y };
      const hiddenBranchData = addHiddenBranchNodes(labelSteps, position, targetId, mergeNodeid);
      const newNodes = hiddenBranchData?.labelNodes;
      const newEdges = hiddenBranchData?.labelEdges;
      const hiddenMergeNode = {
         position,
         id: mergeNodeid,
         type: NODE_TYPES.HIDDEN,
         data: {
            isBranch: !!newNode?.data?.isBranch,
            parentNodeId: newNode.id,
            mergeNodeParent: newNode.id
         }, //Parent node id for identifying the parent node of the hidden merge node
         style: { opacity: 0 }
      };
      newNode.data.mergeId = mergeNodeid;

      return {
         labelNodes: [...newNodes, hiddenMergeNode],
         labelEdges: [...newEdges, generateEdge(mergeNodeid, targetId)]
      };
   };

   const addHiddenBranchNodes = ({ labelNodes = [], labelEdges = [] }, position, targetId, mergeNodeId) => {
      const hiddenNodes = [];
      const hiddenNodeEdges = [];

      labelNodes.forEach((node) => {
         const newId = generateNodeId();
         const hiddenNode = {
            id: newId,
            position: position,
            type: NODE_TYPES.HIDDEN,
            data: { isBranch: true },
            style: { opacity: 0 }
         };

         labelEdges.forEach((edge) => {
            if (edge.source === node.id && edge.target === targetId) {
               edge.target = newId;
               edge.id = `${edge.source}->${newId}`;
            }
         });
         hiddenNodes.push(hiddenNode);
         const outgoingEdge = generateEdge(newId, mergeNodeId, EDGE_TYPES.HIDDEN);
         hiddenNodeEdges.push(outgoingEdge);
      });

      return {
         labelNodes: [...labelNodes, ...hiddenNodes],
         labelEdges: [...labelEdges, ...hiddenNodeEdges]
      };
   };

   const setNewNodes = (newNode, labelNodes) => {
      const nodes = getNodes();
      const newNodes = [...nodes, newNode, ...labelNodes];
      setNodes(newNodes);
   };

   const filterAndSetNewEdges = (newSourceId, newTargetId, targetId, newEdges) => {
      const filteredEdges = filterEdges(newSourceId, newTargetId, targetId);
      setEdges(filteredEdges.concat(newEdges));
   };

   const filterEdges = (newSourceId, newTargetId, targetId) => {
      const edges = getEdges();
      const parentNode = getNode(newSourceId);
      const isParentMultiBranchNode = isMultiBranchNode(parentNode?.data);
      const updatedEdges = edges.filter((edge) => {
         if (isParentMultiBranchNode) {
            return edge.target !== targetId;
         }
         if (edge.type === EDGE_TYPES.FLOATING || edge.type === EDGE_TYPES.STRAIGHT) return true;

         return edge.source !== newSourceId && edge.target !== newTargetId;
      });

      return updatedEdges;
   };

   const updateSteps = (nodeId, componentType) => {
      let stepNo = getNodes().filter((n) => n.type !== NODE_TYPES.STATIC && n.type !== NODE_TYPES.PLACEHOLDER && n.type !== NODE_TYPES.HIDDEN && n.type !== NODE_TYPES.LABEL).length + 1;
      dispatch(
         updateFlowSteps({
            stepNumber: stepNo,
            component: componentType || "",
            elementId: nodeId,
            action: "",
            remarks: "",
            data: {},
            nextStep: ++stepNo
         })
      );
   };

   const moveNode = (nodeData, newSourceId, newTargetId) => {
      const nodes = getNodes();
      const edges = getEdges().filter((edge) => edge.type !== EDGE_TYPES.FLOATING && edge.type !== EDGE_TYPES.STRAIGHT);
      const getNodeData = nodes.find((node) => node.id === nodeData.id);

      const newSourceData = getNode(newSourceId);
      const newTargetData = getNode(newTargetId);

      const newSourceEdge = getConnectedEdges([newSourceData, newTargetData], edges)?.find((edge) => edge.source === newSourceId);

      const edgeUpdates = [];

      if (nodeData.mergeId) {
         const incomers = getIncomers(getNodeData, nodes, edges);
         const incomersConnectedEdges = getConnectedEdges([getNodeData], edges)?.find((edge) => edge.target === getNodeData.id);

         const mergeNodeData = getNode(nodeData.mergeId);
         const outgoers = getOutgoers(mergeNodeData, nodes, edges);

         const outgoersConnectedEdges = getConnectedEdges([mergeNodeData], edges)?.find((connectedEdge) => connectedEdge.source === mergeNodeData.id);

         edgeUpdates.push({
            oldEdge: outgoersConnectedEdges,
            newConn: { source: mergeNodeData.id, target: newTargetId }
         });
         edgeUpdates.push({
            oldEdge: incomersConnectedEdges,
            newConn: { source: incomers[0].id, target: outgoers[0].id }
         });
         edgeUpdates.push({
            oldEdge: newSourceEdge,
            newConn: { source: newSourceId, target: getNodeData.id }
         });
      } else {
         const incomers = getIncomers(getNodeData, nodes, edges);
         const incomersConnectedEdges = getConnectedEdges([getNodeData], edges)?.find((edge) => edge.source === incomers[0].id);

         const outgoers = getOutgoers(getNodeData, nodes, edges);
         const outgoersConnectedEdges = getConnectedEdges([getNodeData], edges)?.find((edge) => edge.target === outgoers[0].id);

         edgeUpdates.push({
            oldEdge: incomersConnectedEdges,
            newConn: { source: incomers[0].id, target: outgoers[0].id }
         });
         edgeUpdates.push({
            oldEdge: newSourceEdge,
            newConn: { source: newSourceId, target: getNodeData.id }
         });
         edgeUpdates.push({
            oldEdge: outgoersConnectedEdges,
            newConn: { source: getNodeData.id, target: newTargetId }
         });
      }

      const nodeUpdate = {
         id: getNodeData.id,
         isBranch: newSourceData?.data?.isBranch
      };

      flushSync(() => {
         setEdges((edges) => {
            return edgeUpdates.reduce((acc, { oldEdge, newConn }) => reconnectEdge(oldEdge, newConn, acc), edges);
         });

         setNodes((nodes) =>
            nodes.map((node) => {
               if (node.id === nodeUpdate.id) {
                  return {
                     ...node,
                     data: { ...node.data, isBranch: nodeUpdate.isBranch }
                  };
               }
               return node;
            })
         );
      });

      updateLayout();
   };

   const willCreateCycleWithBranchCheck = (draggedNodeId, targetNodeId) => {
      if (!targetNodeId || draggedNodeId === targetNodeId) return true;

      const draggedNodeData = getNode(draggedNodeId);

      if (draggedNodeData?.data?.type !== COMMON_COMPONENT_TYPES.CONDITIONAL && draggedNodeData?.data?.type !== COMMON_COMPONENT_TYPES.SELECTION) {
         return false;
      }

      const branchNodes = getChildNodeIdsBeforeMergeNode(draggedNodeData);

      return branchNodes.includes(targetNodeId);
   };

   return {
      tryToGenerateNodeData,
      tryToAddStepToCanvas,
      addHiddenBranchNodes,
      moveNode,
      willCreateCycleWithBranchCheck
   };
};

export default useFlowStepConnection;
